import React, { useState, useEffect, useCallback, useId } from 'react';
import { userService } from '../services/api';
import {
  Shield, ShieldCheck, Key, Plus, X, Clock, Check, User
} from 'lucide-react';

/**
 * UserPermissionsPicker - Modal component for managing user permissions
 * Shows both role-based and custom (user-level) permissions
 */
const UserPermissionsPicker = ({
  user,
  onClose,
  onSave,
  showNotification
}) => {
  const [activeTab, setActiveTab] = useState('roles'); // 'roles' | 'permissions'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Role management state
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);

  // Permission management state
  const [allPermissions, setAllPermissions] = useState([]);
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  const [customPermissions, setCustomPermissions] = useState([]);
  const [showAddPermission, setShowAddPermission] = useState(false);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [grantReason, setGrantReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [permissionSearch, setPermissionSearch] = useState('');
  const modalTitleId = useId();
  const grantTitleId = useId();

  // Group permissions by resource_type
  const groupPermissionsByType = (permissions) => {
    const grouped = {};
    permissions.forEach(perm => {
      const type = perm.resource_type || 'general';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(perm);
    });
    return grouped;
  };

  const fetchRoles = useCallback(async () => {
    try {
      const [rolesRes, userRolesRes] = await Promise.all([
        userService.getRoles(),
        userService.getUserRoles(user.id)
      ]);
      setAvailableRoles(rolesRes.data);
      setUserRoles(userRolesRes.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      showNotification?.('Error', 'Failed to fetch roles', 'error');
    }
  }, [user.id, showNotification]);

  const fetchPermissions = useCallback(async () => {
    try {
      const [allPermsRes, effectiveRes, customRes] = await Promise.all([
        userService.getAllPermissions(),
        userService.getEffectivePermissions(user.id),
        userService.getCustomPermissions(user.id)
      ]);
      setAllPermissions(allPermsRes.data);
      setEffectivePermissions(effectiveRes.data.permissions);
      setCustomPermissions(customRes.data);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      showNotification?.('Error', 'Failed to fetch permissions', 'error');
    }
  }, [user.id, showNotification]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoles(), fetchPermissions()]);
      setLoading(false);
    };
    loadData();
  }, [fetchRoles, fetchPermissions]);

  const handleSaveRoles = async () => {
    setSaving(true);
    try {
      const assignments = userRoles.map((role, index) => ({
        role_id: role.id,
        firm_id: user.primary_firm_id,
        is_primary: index === 0
      }));

      await userService.updateUserRoles(user.id, { assignments });
      showNotification?.('Success', 'Roles updated successfully', 'success');
      onSave?.();
    } catch (error) {
      console.error('Error saving roles:', error);
      showNotification?.('Error', error.response?.data?.detail || 'Failed to update roles', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleGrantPermissions = async () => {
    if (selectedPermissionIds.length === 0) {
      showNotification?.('Warning', 'Please select at least one permission', 'warning');
      return;
    }

    setSaving(true);
    try {
      const grantData = {
        permission_ids: selectedPermissionIds,
        reason: grantReason || null,
        expires_at: expiresAt || null
      };

      await userService.grantPermissions(user.id, grantData);
      await fetchPermissions();
      setShowAddPermission(false);
      setSelectedPermissionIds([]);
      setGrantReason('');
      setExpiresAt('');
      setPermissionSearch('');
      showNotification?.('Success', 'Permissions granted successfully', 'success');
    } catch (error) {
      console.error('Error granting permissions:', error);
      showNotification?.('Error', error.response?.data?.detail || 'Failed to grant permissions', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokePermission = async (permissionId) => {
    setSaving(true);
    try {
      await userService.revokePermission(user.id, permissionId);
      await fetchPermissions();
      showNotification?.('Success', 'Permission revoked successfully', 'success');
    } catch (error) {
      console.error('Error revoking permission:', error);
      showNotification?.('Error', error.response?.data?.detail || 'Failed to revoke permission', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Get permissions that can be added (not already granted via role or user-level)
  const getAvailableToGrant = () => {
    const grantedNames = new Set(effectivePermissions.map(p => p.name));
    let available = allPermissions.filter(p => !grantedNames.has(p.name));

    // Apply search filter
    if (permissionSearch.trim()) {
      const searchLower = permissionSearch.toLowerCase();
      available = available.filter(p =>
        p.name.toLowerCase().includes(searchLower) ||
        (p.description && p.description.toLowerCase().includes(searchLower)) ||
        (p.resource_type && p.resource_type.toLowerCase().includes(searchLower))
      );
    }

    return available;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getResourceTypeIcon = (type) => {
    const icons = {
      trust: Shield,
      case: Key,
      client: User,
      billing: Shield,
      calendar: Clock,
    };
    return icons[type] || Shield;
  };

  const getResourceTypeColor = (type) => {
    const colors = {
      trust: 'text-green-600 bg-green-100',
      case: 'text-blue-600 bg-blue-100',
      client: 'text-purple-600 bg-purple-100',
      billing: 'text-yellow-600 bg-yellow-100',
      calendar: 'text-orange-600 bg-orange-100',
    };
    return colors[type] || 'text-gray-600 bg-gray-100';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div role="dialog" aria-modal="true" aria-labelledby={modalTitleId} className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 id={modalTitleId} className="text-lg font-semibold text-gray-900">
              Manage Access: {user.first_name} {user.last_name}
            </h3>
            <p className="text-sm text-gray-500">{user.email}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('roles')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'roles'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ShieldCheck className="h-4 w-4 inline mr-2" />
              Roles
            </button>
            <button
              onClick={() => setActiveTab('permissions')}
              className={`px-6 py-3 text-sm font-medium border-b-2 ${
                activeTab === 'permissions'
                  ? 'border-yellow-500 text-yellow-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Key className="h-4 w-4 inline mr-2" />
              Custom Permissions
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'roles' ? (
            /* Roles Tab */
            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Select the roles for this user. Roles determine their base permissions.
              </p>
              {availableRoles.map((role) => {
                const isAssigned = userRoles.some(ur => ur.id === role.id);
                return (
                  <label
                    key={role.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setUserRoles([...userRoles, role]);
                        } else {
                          setUserRoles(userRoles.filter(ur => ur.id !== role.id));
                        }
                      }}
                      className="mt-1 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-gray-900">{role.name}</span>
                      {role.description && (
                        <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            /* Permissions Tab */
            <div className="space-y-6">
              {/* Current Custom Permissions */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">Additional Permissions</h4>
                  <button
                    onClick={() => setShowAddPermission(true)}
                    className="text-sm text-yellow-600 hover:text-yellow-700 flex items-center"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Permission
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  These permissions are granted directly to the user, beyond their role defaults.
                </p>

                {customPermissions.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Key className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No custom permissions granted</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {customPermissions.map((perm) => {
                      const Icon = getResourceTypeIcon(perm.resource_type);
                      return (
                        <div
                          key={perm.id}
                          className="flex items-start justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded ${getResourceTypeColor(perm.resource_type)}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{perm.permission_name}</p>
                              <p className="text-xs text-gray-500">
                                Granted by {perm.granted_by_name} on {formatDate(perm.granted_at)}
                              </p>
                              {perm.reason && (
                                <p className="text-xs text-gray-600 mt-1 italic">"{perm.reason}"</p>
                              )}
                              {perm.expires_at && (
                                <p className="text-xs text-orange-600 mt-1 flex items-center">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Expires: {formatDate(perm.expires_at)}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleRevokePermission(perm.permission_id)}
                            disabled={saving}
                            className="text-red-600 hover:text-red-700 p-1"
                            title="Revoke permission"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Effective Permissions Summary */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">All Effective Permissions</h4>
                <p className="text-sm text-gray-500 mb-4">
                  Combined permissions from roles and custom grants.
                </p>

                {Object.entries(groupPermissionsByType(effectivePermissions)).map(([type, perms]) => (
                  <div key={type} className="mb-4">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      {type}
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {perms.map((perm) => (
                        <span
                          key={perm.permission_id}
                          className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                            perm.source === 'role'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                          title={perm.source === 'role' ? `From role: ${perm.role_name}` : 'User-level grant'}
                        >
                          {perm.source === 'user' && <Key className="h-3 w-3 mr-1" />}
                          {perm.name}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>
          {activeTab === 'roles' && (
            <button
              onClick={handleSaveRoles}
              disabled={saving || userRoles.length === 0}
              className="px-4 py-2 text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Save Roles
            </button>
          )}
        </div>

        {/* Add Permission Modal */}
        {showAddPermission && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div role="dialog" aria-modal="true" aria-labelledby={grantTitleId} className="bg-white rounded-lg w-full max-w-md m-4 max-h-[80vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h4 id={grantTitleId} className="font-semibold text-gray-900">Grant Permissions</h4>
                <button
                  onClick={() => {
                    setShowAddPermission(false);
                    setSelectedPermissionIds([]);
                    setGrantReason('');
                    setExpiresAt('');
                    setPermissionSearch('');
                  }}
                  aria-label="Close"
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Permissions
                  </label>
                  <input
                    type="text"
                    value={permissionSearch}
                    onChange={(e) => setPermissionSearch(e.target.value)}
                    placeholder="Search by name, description, or type..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    autoFocus
                  />
                </div>

                {/* Permission Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Permissions
                    {permissionSearch && (
                      <span className="ml-2 text-gray-500 font-normal">
                        ({getAvailableToGrant().length} matching)
                      </span>
                    )}
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2 space-y-1">
                    {getAvailableToGrant().length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {permissionSearch
                          ? 'No permissions match your search'
                          : 'No additional permissions available to grant'}
                      </p>
                    ) : (
                      Object.entries(groupPermissionsByType(getAvailableToGrant())).map(([type, perms]) => (
                        <div key={type} className="mb-3">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 px-2">
                            {type}
                          </p>
                          {perms.map((perm) => (
                            <label
                              key={perm.id}
                              className="flex items-start space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissionIds.includes(perm.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedPermissionIds([...selectedPermissionIds, perm.id]);
                                  } else {
                                    setSelectedPermissionIds(selectedPermissionIds.filter(id => id !== perm.id));
                                  }
                                }}
                                className="mt-0.5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
                              />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{perm.name}</p>
                                {perm.description && (
                                  <p className="text-xs text-gray-500">{perm.description}</p>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (optional)
                  </label>
                  <input
                    type="text"
                    value={grantReason}
                    onChange={(e) => setGrantReason(e.target.value)}
                    placeholder="e.g., Temporary access for Smith case"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>

                {/* Expiration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At (optional)
                  </label>
                  <input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty for permanent access
                  </p>
                </div>
              </div>

              <div className="px-4 py-3 border-t border-gray-200 flex justify-end space-x-2">
                <button
                  onClick={() => {
                    setShowAddPermission(false);
                    setSelectedPermissionIds([]);
                    setGrantReason('');
                    setExpiresAt('');
                    setPermissionSearch('');
                  }}
                  className="px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGrantPermissions}
                  disabled={saving || selectedPermissionIds.length === 0}
                  className="px-3 py-2 text-sm text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {saving ? 'Granting...' : 'Grant Permissions'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserPermissionsPicker;
